import gql from "graphql-tag";
import {useCallback, useEffect} from "react";

import {RenderState, useRenderState} from "@khanacademy/wonder-blocks-core";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useFastlyQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {useRegionOverride} from "@khan/data-region-override";
import {setCoeditorSelectedTemplate} from "@khan/guide-coeditor-core";
import {setPromptVariable} from "@khan/guide-core-helpers";
import {RequestInfo} from "@khan/shared-core";

import {unloadArticleDetails} from "../save-content-info/load-article-details.ts";
import {unloadExerciseDetails} from "../save-content-info/load-exercise-details.ts";
import {unloadVideoDetails} from "../save-content-info/load-video-details.ts";

import type {getMappedStandardsForContentType} from "./__graphql-types__/index.ts";

type LearnableContentKind =
    | "Article"
    | "Challenge"
    | "Exercise"
    | "Interactive"
    | "Project"
    | "Talkthrough"
    | "Video";

export const CUSTOM_CONTENT_MARKER = "custom";

// The MappedStandardsForContent query is a client side query to get the
// mapped standards of a content item on our content pages.
const REGIONAL_STANDARDS_QUERY: GqlOperation<
    getMappedStandardsForContentType["response"],
    getMappedStandardsForContentType["variables"]
> = gqlOp(gql`
    query getMappedStandardsForContent(
        $contentDescriptor: String!
        $locale: String
        $region: String
    ) {
        standards: standardMappingsForContent(
            contentDescriptor: $contentDescriptor
            locale: $locale
            region: $region
        ) {
            setId
            id
            standardId
            description
            relativeUrl
            setOfStandards {
                id
                isDiscoverable
            }
        }
    }
`);

export function useContentItemPromptVariables(
    contentKind: LearnableContentKind,
    contentId: string,
) {
    const renderState = useRenderState();
    const regionResult = useRegionOverride();

    const contentDescriptor = `${contentKind}:${contentId}`;

    const standardsQueryResult = useFastlyQuery(
        REGIONAL_STANDARDS_QUERY,
        renderState === RenderState.Initial ||
            regionResult.status !== "success" ||
            !contentKind ||
            !contentId
            ? {skip: true}
            : {
                  variables: {
                      contentDescriptor: contentDescriptor,
                      locale: RequestInfo.kaLocale,
                      region: regionResult.regionCode,
                  },
              },
    );

    useEffect(() => {
        if (standardsQueryResult.status !== "success") {
            return;
        }

        const standards = (standardsQueryResult.data?.standards || [])
            .filter(isTruthy)
            .map((s) => `- ${s.setId}: ${s.standardId} (${s.description})`)
            .join("\n");

        setPromptVariable("standards", standards);
    }, [standardsQueryResult]);

    useEffect(() => {
        if (!contentKind || !contentId) {
            return;
        }
    }, [contentKind, contentId]);

    const unsetContentItemPromptVariables = useCallback(() => {
        setCoeditorSelectedTemplate(null);
        setPromptVariable("standards", null);
        unloadExerciseDetails();
        unloadArticleDetails();
        unloadVideoDetails();
    }, []);

    return {unsetContentItemPromptVariables};
}
